<template>
  <div class="cart-page">
    <div class="page-header">
      <div>
        <h1>Мои проекты</h1>
        <p>Управляйте спецификациями и группируйте товары по помещениям</p>
      </div>

      <div class="header-actions">
        <Button label="Новый проект" icon="pi pi-plus" severity="primary" @click="createNewCart" />

        <Dropdown v-if="cartStore.carts.length" v-model="selectedCartId" :options="cartStore.carts" optionLabel="name"
          optionValue="id" placeholder="Выберите проект" class="cart-selector" @change="onCartChange" />

        <Button v-if="cartStore.activeCart" icon="pi pi-pencil" text @click="editCartName" />

        <Button v-if="cartStore.activeCart" icon="pi pi-trash" text severity="danger" @click="deleteCurrentCart" />
      </div>
    </div>

    <div v-if="cartStore.loading" class="state-card">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem" />
      <p>Загрузка...</p>
    </div>

    <div v-else-if="!cartStore.carts.length" class="state-card">
      <i class="pi pi-folder-open" style="font-size: 3rem; color: #cbd5e1" />
      <p>У вас пока нет проектов</p>
      <Button label="Создать первый проект" @click="createNewCart" />
    </div>

    <div v-else-if="!cartStore.activeCart" class="state-card">
      <i class="pi pi-inbox" style="font-size: 3rem; color: #cbd5e1" />
      <p>Выберите проект из списка</p>
    </div>

    <div v-else class="cart-layout">
      <div class="main-content">
        <div v-if="cartStore.plainItems.length" class="no-room-section">
          <div class="section-header">
            <h3>
              <i class="pi pi-folder" />
              Без раздела
            </h3>
            <Tag :value="`${cartStore.plainItems.length} товаров`" />
          </div>

          <div class="items-grid">
            <div v-for="item in cartStore.plainItems" :key="item.id" class="cart-item">
              <div class="item-image" @click="goToProduct(item.productId)">
                <img :src="item.defaultImageUrl || '/placeholder.png'" :alt="item.productName" />
              </div>

              <div class="item-info">
                <div class="item-brand">{{ item.brandName }}</div>
                <div class="item-name" @click="goToProduct(item.productId)">
                  {{ item.productName }}
                </div>
                <div class="item-art">Арт. {{ item.articul }}</div>

                <div v-if="canManageDiscount(item)" class="item-discount">
                  <div class="discount-header">
                    <span class="discount-title">Скидка для клиента</span>
                    <strong>{{ formatPercent(getDiscountDraft(item)) }} /
                      {{ formatPercent(getDiscountSliderMax(item)) }}</strong>
                  </div>

                  <input class="discount-slider" type="range" min="0" :max="getDiscountSliderMax(item)" step="1"
                    :value="getDiscountDraft(item)" :disabled="isDiscountSaving(item.id)" @input="
                      updateDiscountDraft(
                        item.id,
                        $event.target.value,
                        item.maxDiscountPercent,
                      )
                      " @change="applyItemDiscount(item)" />

                  <div class="discount-meta">
                    <span>Закупка: {{ formatPrice(item.purchaseUnitPrice) }}</span>
                    <span>Цена клиента: {{ formatPrice(item.unitPrice) }}</span>
                    <span>Маржа/шт: {{ formatPrice(item.marginPerUnit) }}</span>
                    <span>Маржа всего: {{ formatPrice(item.marginTotal) }}</span>
                  </div>
                </div>
              </div>

              <div class="item-price">
                {{ formatPrice(item.unitPrice) }}
              </div>

              <div class="item-quantity">
                <Button icon="pi pi-minus" size="small" text
                  @click="handleUpdateItemQuantity(item, item.quantity - 1)" />
                <span>{{ item.quantity }}</span>
                <Button icon="pi pi-plus" size="small" text
                  @click="handleUpdateItemQuantity(item, item.quantity + 1)" />
              </div>

              <div class="item-total">
                {{ formatPrice(item.lineTotal) }}
              </div>

              <div class="item-actions">
                <Button icon="pi pi-arrow-right" size="small" text severity="info" @click="openMoveModal(item)"
                  v-tooltip="'Переместить'" />
                <Button icon="pi pi-trash" size="small" text severity="danger" @click="cartStore.removeItem(item.id)"
                  v-tooltip="'Удалить'" />
              </div>
            </div>
          </div>
        </div>

        <div v-for="room in cartStore.enrichedRooms" :key="room.id" class="room-section">
          <div class="section-header">
            <div class="room-header">
              <i class="pi pi-building" />

              <div v-if="editingRoomId === room.id" class="room-edit">
                <InputText v-model="editingRoomName" size="small" @keyup.enter="saveRoom(room.id)" />
                <Button icon="pi pi-check" size="small" text @click="saveRoom(room.id)" />
                <Button icon="pi pi-times" size="small" text @click="cancelEditRoom" />
              </div>

              <div v-else class="room-title">
                <span class="room-name">{{ room.name }}</span>
                <div class="room-actions">
                  <Button icon="pi pi-pencil" size="small" text @click="startEditRoom(room)"
                    v-tooltip="'Переименовать'" />
                  <Button icon="pi pi-trash" size="small" text severity="danger" @click="deleteRoom(room.id)"
                    v-tooltip="'Удалить раздел'" />
                </div>
              </div>
            </div>

            <div class="room-stats">
              <Tag :value="`${room.cartItems.length} товаров`" />
              <span class="room-total">{{ formatPrice(room.roomTotal) }}</span>
            </div>
          </div>

          <div v-if="!room.cartItems.length" class="empty-room">
            <i class="pi pi-box" />
            <p>Нет товаров в этом разделе</p>
            <Button label="Добавить товары" size="small" text @click="openProductCatalog" />
          </div>

          <div v-else class="items-grid">
            <div v-for="item in room.cartItems" :key="item.id" class="cart-item">
              <div class="item-image" @click="goToProduct(item.productId)">
                <img :src="item.defaultImageUrl || '/placeholder.png'" :alt="item.productName" />
              </div>

              <div class="item-info">
                <div class="item-brand">{{ item.brandName }}</div>
                <div class="item-name" @click="goToProduct(item.productId)">
                  {{ item.productName }}
                </div>
                <div class="item-art">Арт. {{ item.articul }}</div>

                <div v-if="canManageDiscount(item)" class="item-discount">
                  <div class="discount-header">
                    <span class="discount-title">Скидка для клиента</span>
                    <strong>{{ formatPercent(getDiscountDraft(item)) }} /
                      {{ formatPercent(getDiscountSliderMax(item)) }}</strong>
                  </div>

                  <input class="discount-slider" type="range" min="0" :max="getDiscountSliderMax(item)" step="1"
                    :value="getDiscountDraft(item)" :disabled="isDiscountSaving(item.id)" @input="
                      updateDiscountDraft(
                        item.id,
                        $event.target.value,
                        item.maxDiscountPercent,
                      )
                      " @change="applyItemDiscount(item)" />

                  <div class="discount-meta">
                    <span>Закупка: {{ formatPrice(item.purchaseUnitPrice) }}</span>
                    <span>Цена клиента: {{ formatPrice(item.unitPrice) }}</span>
                    <span>Маржа/шт: {{ formatPrice(item.marginPerUnit) }}</span>
                    <span>Маржа всего: {{ formatPrice(item.marginTotal) }}</span>
                  </div>
                </div>
              </div>

              <div class="item-price">
                {{ formatPrice(item.unitPrice) }}
              </div>

              <div class="item-quantity">
                <Button icon="pi pi-minus" size="small" text
                  @click="handleUpdateItemQuantity(item, item.quantity - 1)" />
                <span>{{ item.quantity }}</span>
                <Button icon="pi pi-plus" size="small" text
                  @click="handleUpdateItemQuantity(item, item.quantity + 1)" />
              </div>

              <div class="item-total">
                {{ formatPrice(item.lineTotal) }}
              </div>

              <div class="item-actions">
                <Button icon="pi pi-arrow-right" size="small" text severity="info" @click="openMoveModal(item, room.id)"
                  v-tooltip="'Переместить'" />
                <Button icon="pi pi-trash" size="small" text severity="danger" @click="cartStore.removeItem(item.id)"
                  v-tooltip="'Удалить'" />
              </div>
            </div>
          </div>
        </div>

        <div class="add-room-card">
          <div v-if="!isAddingRoom" class="add-room-trigger">
            <Button label="Добавить раздел" icon="pi pi-plus" text @click="isAddingRoom = true" />
          </div>

          <div v-else class="add-room-form">
            <InputText v-model="newRoomName" placeholder="Название раздела (например, Ванная)"
              @keyup.enter="handleAddRoom" autofocus />
            <Button label="Создать" severity="primary" @click="handleAddRoom" />
            <Button label="Отмена" text @click="isAddingRoom = false" />
          </div>
          <small v-if="roomError" class="field-error">{{ roomError }}</small>
        </div>
      </div>

      <div class="sidebar">
        <Card>
          <template #title>
            <div class="sidebar-title">
              <i class="pi pi-shopping-cart" />
              <span>Итого по проекту</span>
            </div>
          </template>

          <template #content>
            <div class="summary-content">
              <div class="summary-row">
                <span>Проект:</span>
                <strong>{{ cartStore.activeCart?.name }}</strong>
              </div>

              <div class="summary-row">
                <span>Разделов:</span>
                <strong>{{ cartStore.enrichedRooms.length }}</strong>
              </div>

              <div class="summary-row">
                <span>Товаров:</span>
                <strong>{{ cartStore.totalQuantity }} шт.</strong>
              </div>

              <Divider />

              <div class="summary-total">
                <span>Общая сумма:</span>
                <strong class="total-amount">
                  {{ formatPrice(cartStore.totalAmount) }}
                </strong>
              </div>

              <Divider />

              <div v-if="isB2BUser" class="commercial-offer-client-card">
                <div>
                  <span>Клиент для КП</span>
                  <strong>{{
                    currentCommercialOfferClientInfo?.clientName || "Не указан"
                  }}</strong>
                  <small v-if="currentCommercialOfferClientInfo?.clientPhone">
                    {{ currentCommercialOfferClientInfo.clientPhone }}
                  </small>
                </div>

                <Button :label="currentCommercialOfferClientInfo ? 'Изменить' : 'Указать'
                  " size="small" text @click="openCommercialOfferClientDialog()" />
              </div>

              <Button label="Сформировать КП" icon="pi pi-file-pdf" class="w-full" severity="secondary" outlined
                :loading="isGeneratingCommercialOffer" :disabled="!cartStore.activeCartId ||
                  cartStore.totalQuantity === 0 ||
                  hasPendingDiscountUpdates
                  " @click="handleCommercialOfferButtonClick" />

              <Button label="Оформить заказ" icon="pi pi-check-circle" class="w-full" severity="success"
                :loading="isCreatingOrder" :disabled="!cartStore.activeCartId ||
                  cartStore.totalQuantity === 0 ||
                  hasPendingDiscountUpdates
                  " @click="handleCheckout" />
            </div>
          </template>
        </Card>
      </div>
    </div>

    <Dialog v-model:visible="moveModalVisible" header="Переместить товар" :modal="true"
      :style="{ width: 'min(450px, 95vw)' }">
      <div class="move-modal-content">
        <div class="moving-item">
          <img :src="movingItem?.defaultImageUrl || '/placeholder.png'" class="moving-item-image" />
          <div>
            <div class="moving-item-name">{{ movingItem?.productName }}</div>
            <div class="moving-item-brand">{{ movingItem?.brandName }}</div>
          </div>
        </div>

        <div class="field">
          <label>Выберите раздел:</label>
          <Dropdown v-model="selectedTargetRoomId" :options="moveTargetRooms" optionLabel="name" optionValue="id"
            placeholder="Выберите раздел" class="w-full" />
        </div>
        <div class="field" v-if="selectedTargetRoomId === 'new'">
          <label>Название нового раздела:</label>
          <InputText v-model="newRoomForMove" placeholder="Например, Гостиная" />
        </div>

        <small v-if="moveError" class="field-error">{{ moveError }}</small>

        <div class="dialog-footer">
          <Button label="Отмена" text @click="moveModalVisible = false" />
          <Button label="Переместить" severity="primary" :loading="isMovingItem" :disabled="isMovingItem"
            @click="confirmMoveItem" />
        </div>
      </div>
    </Dialog>

    <CommercialOfferSettingsDialog v-model:visible="commercialOfferClientDialogVisible"
      @hide="resetCommercialOfferClientDraft"
      :loading="isGeneratingCommercialOffer" :isB2B="isB2BUser" :error="commercialOfferClientError"
      @generate="confirmGenerateCommercialOffer">
      <div class="commercial-offer-client-dialog">
        <div class="field">
          <label for="offer-client-name">Имя клиента</label>
          <InputText id="offer-client-name" v-model.trim="commercialOfferClientName" maxlength="120"
            :disabled="isGeneratingCommercialOffer" class="w-full" />
        </div>
        <div class="field">
          <label for="offer-client-phone">Телефон клиента</label>
          <InputText id="offer-client-phone" v-model="commercialOfferClientPhone" class="w-full"
            :disabled="isGeneratingCommercialOffer" @input="handleCommercialOfferPhoneInput" />
          <small class="hint">Международный формат, максимум 15 цифр</small>
        </div>
      </div>
    </CommercialOfferSettingsDialog>

    <Dialog @hide="resetCheckoutPhoneDraft" v-model:visible="checkoutPhoneDialogVisible" header="ВАШ НОМЕР ТЕЛЕФОНА ДЛЯ ДАЛЬНЕЙШЕЙ СВЯЗИ" :modal="true"
      :closable="!isSavingCheckoutPhone" :style="{ width: 'min(430px, 95vw)' }">
      <div class="checkout-phone-dialog">
        <div class="field">
          <label>Номер телефона</label>
          <InputText v-model="checkoutPhone" class="w-full" autofocus
            :disabled="isSavingCheckoutPhone" @input="handleCheckoutPhoneInput" @keyup.enter="confirmCheckoutPhone" />
          <small class="hint">Международный формат, максимум 15 цифр</small>
        </div>

        <small v-if="checkoutPhoneError" class="field-error">
          {{ checkoutPhoneError }}
        </small>

        <div class="dialog-footer">
          <Button label="Отмена" text :disabled="isSavingCheckoutPhone" @click="checkoutPhoneDialogVisible = false" />
          <Button label="Сохранить и оформить заказ" severity="success" :loading="isSavingCheckoutPhone"
            :disabled="isSavingCheckoutPhone" @click="confirmCheckoutPhone" />
        </div>
      </div>
    </Dialog>

    <Dialog v-model:visible="projectDialogVisible"
      :header="projectDialogMode === 'create' ? 'Создание проекта' : 'Переименование проекта'" :modal="true"
      :style="{ width: 'min(430px, 95vw)' }">
      <div class="project-dialog">
        <div class="field">
          <label>Название проекта</label>
          <InputText v-model.trim="projectName" placeholder="Например, Коттедж 2026" class="w-full" autofocus
            @keyup.enter="confirmProjectDialog" />
        </div>

        <small v-if="projectError" class="field-error">
          {{ projectError }}
        </small>

        <div class="dialog-footer">
          <Button label="Отмена" text @click="closeProjectDialog" />
          <Button :label="projectDialogMode === 'create' ? 'Создать' : 'Сохранить'" severity="primary"
            :loading="isSavingProject" :disabled="isSavingProject" @click="confirmProjectDialog" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { normalizePhone } from "@/shared/lib/phone";
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAppToast } from "@/shared/lib/useAppToast";

import Button from "primevue/button";
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Tag from "primevue/tag";
import Divider from "primevue/divider";
import Dialog from "primevue/dialog";
import CommercialOfferSettingsDialog from "@/modules/cart/components/CommercialOfferSettingsDialog.vue";

import { useCartStore } from "@/modules/cart/store/cart.store";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { updateUser } from "@/modules/auth/api/user.service";
import { useOrdersStore } from "@/modules/orders/store/orders.store";
import { generateCommercialOfferPdf } from "@/modules/cart/api/commercialOffer.service";
import {
  loadCommercialOfferClientInfo,
  saveCommercialOfferClientInfo,
  validateCommercialOfferClientInfo,
} from "@/modules/cart/lib/commercialOfferClientInfo";

const router = useRouter();
const toast = useAppToast();
const cartStore = useCartStore();
const authStore = useAuthStore();
const ordersStore = useOrdersStore();

const isCreatingOrder = ref(false);
const selectedCartId = ref(null);
const isAddingRoom = ref(false);
const newRoomName = ref("");

const editingRoomId = ref(null);
const editingRoomName = ref("");

const moveModalVisible = ref(false);
const movingItem = ref(null);
const selectedTargetRoomId = ref(null);
const newRoomForMove = ref("");
const isGeneratingCommercialOffer = ref(false);
const commercialOfferClientDialogVisible = ref(false);
const commercialOfferClientName = ref("");
const commercialOfferClientPhone = ref("");
const commercialOfferClientError = ref("");
const currentCommercialOfferClientInfo = ref(null);
const checkoutPhoneDialogVisible = ref(false);
const checkoutPhone = ref("");
const checkoutPhoneError = ref("");
const isSavingCheckoutPhone = ref(false);
const isMovingItem = ref(false);
const discountDrafts = ref({});
const discountSavingByItemId = ref({});

const projectDialogVisible = ref(false)
const projectDialogMode = ref("create")
const projectName = ref("")
const projectError = ref("")
const isSavingProject = ref(false)

const roomError = ref("")
const moveError = ref("")

const isAuthenticated = computed(() => !!authStore.user?.id);
const isB2BUser = computed(() => authStore.user?.clientType === "B2B");
const currentUserPhone = computed(() =>
  normalizePhone(authStore.user?.phoneNumber),
);
const hasValidB2BContactPhone = computed(
  () => currentUserPhone.value.isValid && !!currentUserPhone.value.value,
);
const hasPendingDiscountUpdates = computed(() =>
  Object.values(discountSavingByItemId.value).some(Boolean),
);
const hasUnsavedDiscountDrafts = computed(() =>
  cartStore.items.some(
    (item) =>
      canManageDiscount(item) &&
      getDiscountDraft(item) !==
      normalizeDiscountValue(
        item.selectedDiscountPercent,
        item.maxDiscountPercent,
      ),
  ),
);

function loadCurrentCommercialOfferClientInfo() {
  currentCommercialOfferClientInfo.value = loadCommercialOfferClientInfo(
    "cart",
    authStore.user?.id,
    cartStore.activeCartId,
  );
}

function getCurrentCommercialOfferClientInfo() {
  loadCurrentCommercialOfferClientInfo();
  return currentCommercialOfferClientInfo.value;
}

function saveCurrentCommercialOfferClientInfo(clientInfo) {
  const result = saveCommercialOfferClientInfo(
    "cart",
    authStore.user?.id,
    cartStore.activeCartId,
    clientInfo,
  );

  if (result.isValid) {
    currentCommercialOfferClientInfo.value = result.value;
  }

  return result;
}

function setCommercialOfferClientPhone(value) {
  commercialOfferClientPhone.value = value;
  commercialOfferClientError.value = "";
}

function handleCommercialOfferPhoneInput(event) {
  setCommercialOfferClientPhone(
    event?.target?.value ?? "",
  );
}

function setCheckoutPhone(value) {
  checkoutPhone.value = value;
  checkoutPhoneError.value = "";
}

function handleCheckoutPhoneInput(event) {
  setCheckoutPhone(event?.target?.value ?? "");
}

function resetCheckoutPhoneDraft() {
  checkoutPhone.value = "";
  checkoutPhoneError.value = "";
}

function resetCommercialOfferClientDraft() {
  commercialOfferClientName.value = "";
  commercialOfferClientPhone.value = "";
  commercialOfferClientError.value = "";
}

function openCheckoutPhoneDialog() {
  checkoutPhone.value = currentUserPhone.value.isValid ? currentUserPhone.value.value : "";
  checkoutPhoneError.value = "";
  checkoutPhoneDialogVisible.value = true;
}

const moveTargetRooms = computed(() => {
  const rooms = cartStore.enrichedRooms.map((r) => ({
    id: r.id,
    name: r.name,
  }));

  return [
    ...rooms,
    { id: null, name: "📁 Без раздела" },
    { id: "new", name: "➕ Создать новый раздел" },
  ];
});

function showToast(severity, summary, detail) {
  toast[
    severity === "error"
      ? "error"
      : severity === "success"
        ? "success"
        : severity === "warn"
          ? "warn"
          : "info"
  ](detail, summary);
}

function getQuantityUpdateErrorMessage(error) {
  return error?.fieldErrors?.Quantity || error?.fieldErrors?.quantity ||
    error?.message || "Не удалось обновить количество товара";
}

function normalizeDiscountValue(value, maxDiscountPercent = 0) {
  const numericValue = Number(value ?? 0);
  const numericMax = getRoundedDiscountMax(maxDiscountPercent);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.min(Math.max(Math.round(numericValue), 0), numericMax);
}

function getRoundedDiscountMax(maxDiscountPercent = 0) {
  const numericMax = Number(maxDiscountPercent ?? 0);

  if (!Number.isFinite(numericMax)) {
    return 0;
  }

  return Math.max(Math.floor(numericMax), 0);
}

function getDiscountSliderMax(item) {
  return getRoundedDiscountMax(item?.maxDiscountPercent);
}

async function handleUpdateItemQuantity(item, quantity) {
  try {
    await cartStore.updateItemQuantity(item, quantity);
  } catch (error) {
    showToast("error", "Ошибка", getQuantityUpdateErrorMessage(error));
  }
}

function syncDiscountDrafts() {
  const nextDrafts = {};

  for (const item of cartStore.items) {
    nextDrafts[item.id] = normalizeDiscountValue(
      item.selectedDiscountPercent,
      item.maxDiscountPercent,
    );
  }

  discountDrafts.value = nextDrafts;
}

function canManageDiscount(item) {
  return (
    isB2BUser.value &&
    Boolean(item?.canControlDiscount) &&
    getRoundedDiscountMax(item?.maxDiscountPercent) > 0
  );
}

function getDiscountDraft(item) {
  return (
    discountDrafts.value[item.id] ??
    normalizeDiscountValue(
      item.selectedDiscountPercent,
      item.maxDiscountPercent,
    )
  );
}

function updateDiscountDraft(itemId, value, maxDiscountPercent) {
  discountDrafts.value = {
    ...discountDrafts.value,
    [itemId]: normalizeDiscountValue(value, maxDiscountPercent),
  };
}

function isDiscountSaving(itemId) {
  return Boolean(discountSavingByItemId.value[itemId]);
}

function getItemsWithUnsavedDiscountDrafts() {
  return cartStore.items.filter(
    (item) =>
      canManageDiscount(item) &&
      getDiscountDraft(item) !==
      normalizeDiscountValue(
        item.selectedDiscountPercent,
        item.maxDiscountPercent,
      ),
  );
}

async function persistDiscountDraftsBeforeAction(actionLabel) {
  if (hasPendingDiscountUpdates.value) {
    showToast(
      "warn",
      "Скидка сохраняется",
      `Дождитесь сохранения скидки перед ${actionLabel}`,
    );
    return false;
  }

  const dirtyItems = getItemsWithUnsavedDiscountDrafts();

  for (const item of dirtyItems) {
    const isSaved = await applyItemDiscount(item);

    if (!isSaved) {
      showToast(
        "error",
        "Ошибка",
        `Не удалось сохранить скидку перед ${actionLabel}`,
      );
      return false;
    }
  }

  return true;
}

async function ensureCheckoutPreconditions() {
  if (!cartStore.activeCartId) {
    showToast(
      "warn",
      "Проект не выбран",
      "Пожалуйста, выберите проект для оформления заказа",
    );
    return false;
  }

  if (cartStore.totalQuantity === 0) {
    showToast(
      "warn",
      "Проект пуст",
      "Добавьте товары перед оформлением заказа",
    );
    return false;
  }

  const isReadyForCheckout = await persistDiscountDraftsBeforeAction(
    "оформлением заказа",
  );
  if (!isReadyForCheckout) {
    return false;
  }

  return true;
}

async function submitCheckout() {
  isCreatingOrder.value = true;
  try {
    const result = await ordersStore.createOrder(cartStore.activeCartId);
    const savedClientInfo = getCurrentCommercialOfferClientInfo();

    if (result?.orderId && savedClientInfo) {
      saveCommercialOfferClientInfo(
        "order",
        authStore.user?.id,
        result.orderId,
        savedClientInfo,
      );
    }

    showToast(
      "success",
      "Заказ оформлен",
      `Заказ #${result.orderId} успешно создан`,
    );
    router.push("/cabinet/orders");
  } catch (error) {
    showToast(
      "error",
      "Ошибка",
      ordersStore.error || error.message || "Не удалось оформить заказ",
    );
  } finally {
    isCreatingOrder.value = false;
  }
}

async function handleCheckout() {
  const isReadyForCheckout = await ensureCheckoutPreconditions();
  if (!isReadyForCheckout) {
    return;
  }

  if (isB2BUser.value && !hasValidB2BContactPhone.value) {
    openCheckoutPhoneDialog();
    return;
  }

  await submitCheckout();
}

async function confirmCheckoutPhone() {
  const phoneResult = normalizePhone(checkoutPhone.value);

  if (!phoneResult.isValid) {
    checkoutPhoneError.value = phoneResult.message;
    return;
  }

  if (!phoneResult.value) {
    checkoutPhoneError.value = "Введите номер телефона для дальнейшей связи";
    return;
  }

  if (!authStore.user?.id || isSavingCheckoutPhone.value) {
    return;
  }

  isSavingCheckoutPhone.value = true;

  try {
    await updateUser({
      phoneNumber: phoneResult.value,
    });
    await authStore.fetchMe();
    checkoutPhoneDialogVisible.value = false;
    showToast("success", "Номер сохранён", "Теперь менеджер сможет связаться с вами");
    await submitCheckout();
  } catch (error) {
    checkoutPhoneError.value =
      error.message || "Не удалось сохранить номер телефона";
  } finally {
    isSavingCheckoutPhone.value = false;
  }
}

function formatPrice(price) {
  const numericPrice = Number(price || 0);

  return (
    new Intl.NumberFormat("ru-KZ", {
      maximumFractionDigits: 0,
    }).format(Number.isFinite(numericPrice) ? Math.round(numericPrice) : 0) +
    " ₸"
  );
}

function formatPercent(value) {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue)) {
    return "0%";
  }

  return `${Math.round(numericValue)}%`;
}

function getCommercialOfferFileName() {
  const cartName =
    cartStore.activeCart?.name?.trim() || `cart-${cartStore.activeCartId}`;
  return `${cartName.replace(/[<>:"/\\|?*]+/g, "-")}.pdf`;
}

function getFileNameFromHeaders(headers) {
  const contentDisposition = headers?.["content-disposition"];

  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return fallbackMatch?.[1] ?? null;
}

function downloadFile(blob, fileName) {
  const fileUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = fileUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(fileUrl);
}

async function extractCommercialOfferError(error) {
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    const text = await responseData.text();

    if (!text) {
      return "Не удалось сформировать КП";
    }

    try {
      const payload = JSON.parse(text);
      return (
        payload?.errors?.[0]?.errors?.[0] ||
        payload?.detail ||
        payload?.title ||
        "Не удалось сформировать КП"
      );
    } catch {
      return text;
    }
  }

  return (
    responseData?.errors?.[0]?.errors?.[0] ||
    responseData?.detail ||
    error?.message ||
    "Не удалось сформировать КП"
  );
}

async function init() {
  if (!isAuthenticated.value) {
    cartStore.clearLocalState();
    selectedCartId.value = null;
    return;
  }

  try {
    await cartStore.loadUserCarts();

    if (!cartStore.carts.length) {
      selectedCartId.value = null;
      return;
    }

    if (cartStore.activeCartId) {
      selectedCartId.value = cartStore.activeCartId;
      await cartStore.selectCart(cartStore.activeCartId);
      return;
    }

    selectedCartId.value = cartStore.carts[0].id;
    await cartStore.selectCart(selectedCartId.value);
  } catch (error) {
    selectedCartId.value = null;
    showToast(
      "error",
      "Ошибка",
      error.message || "Не удалось загрузить проекты",
    );
  }
}

async function onCartChange() {
  if (!selectedCartId.value) return;

  try {
    await cartStore.selectCart(selectedCartId.value);
  } catch (error) {
    showToast(
      "error",
      "Ошибка",
      error.message || "Не удалось переключить проект",
    );
  }
}

function createNewCart() {
  projectDialogMode.value = "create"
  projectName.value = "Новый проект"
  projectError.value = ""
  projectDialogVisible.value = true
}

function editCartName() {
  if (!cartStore.activeCart) return

  projectDialogMode.value = "edit"
  projectName.value = cartStore.activeCart.name || ""
  projectError.value = ""
  projectDialogVisible.value = true
}

function closeProjectDialog() {
  if (isSavingProject.value) return

  projectDialogVisible.value = false
  projectName.value = ""
  projectError.value = ""
}

async function confirmProjectDialog() {
  const name = projectName.value.trim()

  if (!name) {
    projectError.value = "Введите название проекта"
    return
  }

  isSavingProject.value = true
  projectError.value = ""

  try {
    if (projectDialogMode.value === "create") {
      const createdId = await cartStore.createCart(name)

      if (createdId) {
        selectedCartId.value = createdId
        await cartStore.selectCart(createdId)
        showToast("success", "Проект создан", "Новый проект успешно создан")
      }
    } else {
      await cartStore.updateCartName(cartStore.activeCart.id, name)

      if (cartStore.activeCart) {
        selectedCartId.value = cartStore.activeCart.id
      }

      showToast("success", "Название обновлено", "Название проекта изменено")
    }

    closeProjectDialog()
  } catch (error) {
    projectError.value =
      error.message ||
      (projectDialogMode.value === "create"
        ? "Не удалось создать проект"
        : "Не удалось переименовать проект")

    showToast("error", "Ошибка", projectError.value)
  } finally {
    isSavingProject.value = false
  }
}

async function deleteCurrentCart() {
  if (!cartStore.activeCart) return;
  if (!confirm(`Удалить проект "${cartStore.activeCart.name}"?`)) return;

  try {
    const deletedId = cartStore.activeCart.id;
    await cartStore.deleteCart(deletedId);

    if (cartStore.carts.length) {
      selectedCartId.value = cartStore.carts[0].id;
      await cartStore.selectCart(selectedCartId.value);
    } else {
      selectedCartId.value = null;
    }

    showToast("success", "Проект удалён", "Проект успешно удалён");
  } catch (error) {
    showToast("error", "Ошибка", error.message || "Не удалось удалить проект");
  }
}

async function handleAddRoom() {
  roomError.value = "";

  const name = newRoomName.value.trim();
  if (!name || !cartStore.activeCartId) {
    roomError.value = "Введите название раздела";
    return;
  }

  try {
    await cartStore.createRoom({
      cartId: cartStore.activeCartId,
      name,
    });

    newRoomName.value = "";
    isAddingRoom.value = false;
    showToast("success", "Раздел создан", "Новый раздел успешно добавлен");
  } catch (error) {
    roomError.value = error.message || "Не удалось создать раздел";
    showToast("error", "Ошибка", roomError.value);
  }
}

function startEditRoom(room) {
  editingRoomId.value = room.id;
  editingRoomName.value = room.name;
}

function cancelEditRoom() {
  editingRoomId.value = null;
  editingRoomName.value = "";
}

async function saveRoom(roomId) {
  if (!editingRoomName.value.trim()) return;

  try {
    await cartStore.updateRoomName(roomId, editingRoomName.value.trim());
    editingRoomId.value = null;
    editingRoomName.value = "";
    showToast("success", "Раздел обновлён", "Название раздела изменено");
  } catch (error) {
    showToast(
      "error",
      "Ошибка",
      error.message || "Не удалось переименовать раздел",
    );
  }
}

async function deleteRoom(roomId) {
  if (
    !confirm("Удалить этот раздел? Все товары переместятся в 'Без раздела'")
  ) {
    return;
  }

  try {
    const room = cartStore.enrichedRooms.find((r) => r.id === roomId);

    if (room && room.cartItems.length) {
      for (const item of room.cartItems) {
        await cartStore.updateItemQuantity(item, item.quantity, null);
      }
    }

    await cartStore.deleteRoom(roomId);
    showToast("success", "Раздел удалён", "Раздел успешно удалён");
  } catch (error) {
    showToast("error", "Ошибка", error.message || "Не удалось удалить раздел");
  }
}

function goToProduct(productId) {
  if (!productId) return;
  router.push(`/products/${productId}`);
}

function openProductCatalog() {
  router.push("/products");
}

async function handleCommercialOfferButtonClick() {
  const isReadyForOffer = await persistDiscountDraftsBeforeAction(
    "формированием КП",
  );
  if (!isReadyForOffer) {
    return;
  }

  openCommercialOfferClientDialog();
}

function openCommercialOfferClientDialog() {
  if (isGeneratingCommercialOffer.value) return;
  const savedClientInfo = getCurrentCommercialOfferClientInfo();
  commercialOfferClientName.value = savedClientInfo?.clientName || "";
  commercialOfferClientPhone.value = savedClientInfo?.clientPhone || "";
  commercialOfferClientError.value = "";
  commercialOfferClientDialogVisible.value = true;
}

async function confirmGenerateCommercialOffer() {
  if (isGeneratingCommercialOffer.value) return;
  commercialOfferClientError.value = "";
  let options = {};
  if (isB2BUser.value) {
    const validation = validateCommercialOfferClientInfo({
      clientName: commercialOfferClientName.value,
      clientPhone: commercialOfferClientPhone.value,
    });
    if (!validation.isValid) {
      commercialOfferClientError.value = validation.message;
      return;
    }
    const saveResult = saveCurrentCommercialOfferClientInfo(validation.value);
    if (!saveResult.isValid) {
      commercialOfferClientError.value = saveResult.message;
      return;
    }
    options = saveResult.value;
  }
  await handleGenerateCommercialOffer(options);
}

async function handleGenerateCommercialOffer(options = {}) {
  if (!cartStore.activeCartId || isGeneratingCommercialOffer.value) {
    return;
  }

  if (hasPendingDiscountUpdates.value || hasUnsavedDiscountDrafts.value) {
    showToast(
      "warn",
      "Скидка сохраняется",
      "Дождитесь сохранения скидки перед формированием КП",
    );
    return;
  }

  isGeneratingCommercialOffer.value = true;

  try {
    const response = await generateCommercialOfferPdf(
      cartStore.activeCartId,
      options,
    );
    const fileName =
      getFileNameFromHeaders(response.headers) || getCommercialOfferFileName();

    downloadFile(response.data, fileName);
    commercialOfferClientDialogVisible.value = false;
    showToast("success", "КП сформировано", "PDF успешно скачан");
  } catch (error) {
    const detail = await extractCommercialOfferError(error);
    commercialOfferClientError.value = detail;
    showToast("error", "Ошибка", detail);
  } finally {
    isGeneratingCommercialOffer.value = false;
  }
}

async function applyItemDiscount(item) {
  if (!canManageDiscount(item) || isDiscountSaving(item.id)) {
    return false;
  }

  const selectedDiscountPercent = normalizeDiscountValue(
    getDiscountDraft(item),
    item.maxDiscountPercent,
  );
  const currentDiscountPercent = normalizeDiscountValue(
    item.selectedDiscountPercent,
    item.maxDiscountPercent,
  );

  if (selectedDiscountPercent === currentDiscountPercent) {
    updateDiscountDraft(
      item.id,
      selectedDiscountPercent,
      item.maxDiscountPercent,
    );
    return true;
  }

  let isSaved = false;

  discountSavingByItemId.value = {
    ...discountSavingByItemId.value,
    [item.id]: true,
  };

  try {
    await cartStore.updateItemDiscount(item.id, selectedDiscountPercent);
    isSaved = true;
  } catch (error) {
    updateDiscountDraft(
      item.id,
      currentDiscountPercent,
      item.maxDiscountPercent,
    );
    showToast(
      "error",
      "Ошибка",
      error.message || "Не удалось обновить скидку для товара",
    );
  } finally {
    const nextSavingState = { ...discountSavingByItemId.value };
    delete nextSavingState[item.id];
    discountSavingByItemId.value = nextSavingState;
  }

  return isSaved;
}

function openMoveModal(item, currentRoomId = null) {
  movingItem.value = item;
  selectedTargetRoomId.value = currentRoomId !== null ? currentRoomId : null;
  newRoomForMove.value = "";
  moveError.value = "";
  moveModalVisible.value = true;
}

async function confirmMoveItem() {
  if (!movingItem.value) return;

  try {
    isMovingItem.value = true;
    moveError.value = "";

    let targetRoomId = selectedTargetRoomId.value;

    if (targetRoomId === "new") {
      if (!newRoomForMove.value.trim()) {
        moveError.value = "Введите название нового раздела";
        return;
      }

      await cartStore.createRoom({
        cartId: cartStore.activeCartId,
        name: newRoomForMove.value.trim(),
      });

      await cartStore.selectCart(cartStore.activeCartId);

      const newRoom = cartStore.enrichedRooms.find(
        (r) => r.name === newRoomForMove.value.trim(),
      );
      targetRoomId = newRoom?.id || null;
    }

    await cartStore.updateItemQuantity(
      movingItem.value,
      movingItem.value.quantity,
      targetRoomId,
    );

    moveModalVisible.value = false;
    movingItem.value = null;
    selectedTargetRoomId.value = null;
    newRoomForMove.value = "";
    moveError.value = "";

    showToast("success", "Товар перемещён", "Товар успешно перемещён");
  } catch (error) {
    moveError.value = error.message || "Не удалось переместить товар";
    showToast("error", "Ошибка", moveError.value);
  } finally {
    isMovingItem.value = false;
  }
}

watch(
  () =>
    cartStore.items.map(
      (item) =>
        `${item.id}:${item.selectedDiscountPercent}:${item.maxDiscountPercent}`,
    ),
  () => {
    syncDiscountDrafts();
  },
  { immediate: true },
);

watch(
  () => authStore.user?.id,
  () => {
    init();
  },
);

watch(
  () => [authStore.user?.id, cartStore.activeCartId],
  () => {
    loadCurrentCommercialOfferClientInfo();
  },
  { immediate: true },
);

onMounted(() => {
  init();
});
</script>

<style scoped>
.cart-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
  min-height: 100vh;
  background: #f8fafc;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.page-header h1 {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

.page-header p {
  margin: 0;
  color: #64748b;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.cart-selector {
  min-width: 200px;
}

.state-card {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.state-card i {
  margin-bottom: 16px;
}

.state-card p {
  margin: 0 0 20px;
  color: #64748b;
}

.cart-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 32px;
  align-items: start;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.no-room-section,
.room-section {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e293b;
}

.room-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.room-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.room-name {
  font-weight: 600;
  font-size: 16px;
}

.room-actions {
  display: flex;
  gap: 4px;
}

.room-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.room-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.room-total {
  font-weight: 700;
  font-size: 18px;
  color: #0f172a;
}

.items-grid {
  display: flex;
  flex-direction: column;
}

.cart-item {
  display: grid;
  grid-template-columns: 80px 1fr 120px 120px 120px 60px;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.cart-item:hover {
  background: #fafcff;
}

.item-image {
  width: 70px;
  height: 70px;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
  cursor: pointer;
  flex-shrink: 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-info {
  min-width: 0;
}

.item-brand {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.item-name {
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 4px;
  cursor: pointer;
}

.item-name:hover {
  color: #3b82f6;
  text-decoration: underline;
}

.item-art {
  font-size: 11px;
  color: #94a3b8;
}

.item-discount {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #dbe4ee;
  background: #f8fafc;
}

.discount-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #475569;
}

.discount-title {
  font-weight: 600;
  color: #334155;
}

.discount-slider {
  width: 100%;
  accent-color: #0f766e;
}

.discount-slider:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.discount-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  margin-top: 8px;
  font-size: 11px;
  color: #64748b;
}

.discount-meta span {
  min-width: 0;
  line-height: 1.4;
}

.item-price,
.item-total {
  font-weight: 500;
  color: #0f172a;
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-quantity span {
  min-width: 24px;
  text-align: center;
  font-weight: 500;
}

.item-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.empty-room {
  text-align: center;
  padding: 48px 20px;
  color: #94a3b8;
}

.empty-room i {
  font-size: 2.5rem;
  margin-bottom: 12px;
}

.add-room-card {
  background: white;
  border-radius: 16px;
  padding: 16px 20px;
  border: 1px dashed #cbd5e1;
  text-align: center;
}

.add-room-form {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.add-room-form :deep(.p-inputtext) {
  flex: 1;
  max-width: 300px;
}

.sidebar {
  position: sticky;
  top: 24px;
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
}

.commercial-offer-client-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #f8fafc;
}

.commercial-offer-client-card div {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.commercial-offer-client-card span,
.commercial-offer-client-card small {
  color: #64748b;
  font-size: 12px;
}

.commercial-offer-client-card strong {
  color: #0f172a;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.total-amount {
  font-size: 24px;
  color: #22c55e;
}

.w-full {
  width: 100%;
}

.move-modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.commercial-offer-client-dialog,
.checkout-phone-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.moving-item {
  display: flex;
  gap: 16px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
}

.moving-item-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
}

.moving-item-name {
  font-weight: 600;
}

.moving-item-brand {
  font-size: 12px;
  color: #64748b;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 500;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 1100px) {
  .cart-item {
    grid-template-columns: 70px 1fr 100px 100px 100px 50px;
    gap: 12px;
  }
}

@media (max-width: 900px) {
  .cart-layout {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: static;
  }

  .cart-item {
    grid-template-columns: 70px 1fr;
    grid-template-areas:
      "image info"
      "price price"
      "quantity quantity"
      "total total"
      "actions actions";
  }

  .item-image {
    grid-area: image;
  }

  .item-info {
    grid-area: info;
  }

  .item-price {
    grid-area: price;
  }

  .item-quantity {
    grid-area: quantity;
  }

  .item-total {
    grid-area: total;
  }

  .item-actions {
    justify-content: flex-start;
    margin-left: 0;
    margin-top: 8px;
    padding-left: 0;
  }
}

.field-error {
  color: #dc2626;
  font-size: 13px;
}

@media (max-width: 600px) {
  .cart-page {
    padding: 20px 16px;
  }

  .page-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .cart-selector {
    flex: 1;
  }

  .section-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .discount-meta {
    grid-template-columns: 1fr;
  }

  .item-actions {
    flex-wrap: wrap;
    gap: 8px;
  }

  .field label {
    font-weight: 500;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
  }

  .project-dialog {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .project-dialog :deep(.p-inputtext) {
    border-radius: 12px;
    padding: 12px 14px;
  }

  .project-dialog .field label {
    color: #334155;
    font-size: 14px;
    font-weight: 600;
  }

  /* RESPONSIVE */
  @media (max-width: 1100px) {
    .cart-item {
      grid-template-columns: 70px 1fr 100px 100px 100px 50px;
      gap: 12px;
    }
  }

  @media (max-width: 900px) {
    .cart-layout {
      grid-template-columns: 1fr;
    }

    .sidebar {
      position: static;
    }

    .cart-item {
      grid-template-columns: 70px 1fr;
      grid-template-areas:
        "image info"
        "price price"
        "quantity quantity"
        "total total"
        "actions actions";
    }

    .item-image {
      grid-area: image;
    }

    .item-info {
      grid-area: info;
    }

    .item-price {
      grid-area: price;
    }

    .item-quantity {
      grid-area: quantity;
    }

    .item-total {
      grid-area: total;
    }

    .item-actions {
      grid-area: actions;
      justify-content: flex-end;
    }
  }

  .field-error {
  color: #dc2626;
  font-size: 13px;
}

@media (max-width: 600px) {
    .cart-page {
      padding: 20px 16px;
    }

    .page-header {
      flex-direction: column;
    }

    .header-actions {
      width: 100%;
    }

    .cart-selector {
      flex: 1;
    }

    .section-header {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }

    .discount-meta {
      grid-template-columns: 1fr;
    }
  }
}
</style>
